// src/pages/TeamTasksPage.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Plus, Users, Tag, Loader2, ChevronRight, Calendar, GripVertical,
  CheckCircle2, Hash, X, Trash2, Settings, Shield, Edit, Save,
  LayoutDashboard, List, Kanban, GanttChart, MessageSquare, Paperclip,
  Star, ChevronDown as ChevronDownIcon, MoreVertical, Clock, TrendingUp, Vote, Gauge
} from 'lucide-react';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragStartEvent,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useDroppable } from '@dnd-kit/core';
import { RootState } from '../store/store';
import { selectUser } from '../store/slices/authSlice';
import { API_BASE_URL } from '../config/api';
import ProjectNavbar from '../components/ProjectNavbar';

interface MemberPermission {
  memberId: string;
  permissions: {
    watch: boolean;
    create: boolean;
    edit: boolean;
    delete: boolean;
  };
}

interface Team {
  _id: string;
  name: string;
  description?: string;
  members: { _id: string; name: string; email: string }[];
  creator?: { _id: string; name: string; email: string };
  memberPermissions?: MemberPermission[];
}

interface Task {
  _id: string;
  title: string;
  description: string;
  status: 'todo' | 'in-progress' | 'review' | 'done' | 'blocked' | 'archived';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  assignee: { _id: string; name: string } | null;
  deadline?: string;
  tags: string[];
  progress: number;
  isOverdue: boolean;
  estimatedDuration?: number;
  createdBy?: string | { _id: string; name: string; email: string };
  subTasks: {
    title: string;
    status: string;
    assignee: { _id: string; name: string } | null;
  }[];
}

const API_BASE = API_BASE_URL;

const getMemberId = (member: any): string => {
  if (!member) return '';
  if (typeof member === 'string') return member;
  if (typeof member === 'object' && '_id' in member) return (member as any)._id;
  return '';
};

const buildDefaultPermissions = (team: Team): MemberPermission[] => {
  return (team.members || []).map((member: any) => ({
    memberId: getMemberId(member),
    permissions: {
      watch: true,
      create: false,
      edit: false,
      delete: false
    }
  })).filter(perm => perm.memberId);
};

const normalizePermissionsList = (permissions: any[]): MemberPermission[] => {
  return permissions.map(perm => {
    const memberId = getMemberId(perm.memberId);
    if (!memberId) return null;
    return {
      memberId,
      permissions: perm.permissions
    };
  }).filter(Boolean) as MemberPermission[];
};

interface ProjectData {
  _id: string;
  title: string;
  description: string;
  start: string;
  end: string;
  type: 'meeting' | 'workshop' | 'deadline' | 'social' | 'project';
  isPublic: boolean;
  color: string;
  creator: { _id: string; name: string; email: string };
  teams?: Team[];
}

const TeamTasksPage: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
  const { projectId } = useParams<{ projectId?: string }>();
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => selectUser(state));
  const [project, setProject] = useState<ProjectData | null>(null);
  const [projectLoading, setProjectLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'list' | 'board' | 'timeline' | 'dashboard' | 'calendar' | 'workflow' | 'messages' | 'files'>('board');
  const [teams, setTeams] = useState<Team[]>([]);
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [tasksByStatus, setTasksByStatus] = useState<Record<string, Task[]>>({
    todo: [], 'in-progress': [], review: [], done: [], blocked: [], archived: []
  });
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [expandedTasks, setExpandedTasks] = useState<Set<string>>(new Set());
  const [teamProgress, setTeamProgress] = useState<Record<string, number>>({});
  const [activeTask, setActiveTask] = useState<Task | null>(null);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState<string | null>(null);
  const [memberPermissions, setMemberPermissions] = useState<MemberPermission[]>([]);
  const [timeEstimationVotes, setTimeEstimationVotes] = useState<Record<string, any>>({});
  const [showEstimationModal, setShowEstimationModal] = useState(false);
  const [selectedTaskForEstimation, setSelectedTaskForEstimation] = useState<Task | null>(null);
  const [selectedEstimationOption, setSelectedEstimationOption] = useState<number | null>(null);
  const [customHours, setCustomHours] = useState<string>('');
  const [estimationLoading, setEstimationLoading] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    status: 'todo' as Task['status'],
    priority: 'medium' as Task['priority'],
    deadline: '',
    assignee: '',
    tags: [] as string[],
    subTasks: [] as { title: string; status: string; assignee: string }[]
  });
  const userId = user?._id;
  const isProjectContext = Boolean(projectId);

  const organizeTasksByStatus = useCallback((taskList: Task[]) => {
    const organized: Record<string, Task[]> = {
      todo: [], 'in-progress': [], review: [], done: [], blocked: [], archived: []
    };
    const seen = new Set();
    taskList.forEach(t => {
      if (!seen.has(t._id)) {
        seen.add(t._id);
        if (organized[t.status]) organized[t.status].push(t);
      }
    });
    setTasksByStatus(organized);
  }, [setTasksByStatus]);

  const initializePermissionsForTeam = useCallback((team: Team) => {
    if (team.memberPermissions && team.memberPermissions.length > 0) {
      setMemberPermissions(normalizePermissionsList(team.memberPermissions));
    } else {
      setMemberPermissions(buildDefaultPermissions(team));
    }
  }, [setMemberPermissions]);

  // === TIME ESTIMATION VOTE ===
  const fetchTimeEstimationVote = useCallback(async (taskId: string) => {
    try {
      const res = await axios.get(`${API_BASE}/votes/task/${taskId}/time-estimation`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.data.success) {
        setTimeEstimationVotes(prev => ({ ...prev, [taskId]: res.data.data }));
      }
    } catch (err: any) {
      // Si 404, pas de vote d'estimation pour cette tâche
      if (err.response?.status !== 404) {
        console.error('Erreur récupération vote estimation:', err);
      }
    }
  }, []);

  const fetchTasksForContext = useCallback(async (team?: Team) => {
    if (!userId) return;
    if (!isProjectContext && !team) return;

    setLoading(true);
    try {
      let fetchedTasks: Task[] = [];
      if (isProjectContext && projectId) {
        const res = await axios.get(
          `${API_BASE}/tasks/${projectId}?userId=${userId}`,
          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
        );
        const data = res.data?.data;
        if (data?.tasks && Array.isArray(data.tasks)) {
          fetchedTasks = data.tasks;
        } else if (Array.isArray(data)) {
          fetchedTasks = data;
        }
      } else if (team) {
        const res = await axios.get(`${API_BASE}/tasks/team/${team._id}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        fetchedTasks = Array.isArray(res.data.data?.tasks) ? res.data.data.tasks : [];
      }
      setTasks(fetchedTasks);
      organizeTasksByStatus(fetchedTasks);
      
      // Charger les votes d'estimation pour toutes les tâches
      for (const task of fetchedTasks) {
        await fetchTimeEstimationVote(task._id);
      }
    } catch (err) {
      console.error('Erreur chargement tâches:', err);
      setTasks([]);
    } finally {
      setLoading(false);
    }
  }, [isProjectContext, organizeTasksByStatus, projectId, userId, fetchTimeEstimationVote]);

  // Fetch project data if projectId is provided
  useEffect(() => {
    if (projectId && user?._id) {
      const fetchProject = async () => {
        setProjectLoading(true);
        try {
          const res = await axios.get(`${API_BASE}/projects/${projectId}`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          });
          setProject(res.data.data);
        } catch (err: any) {
          console.error('Erreur chargement projet:', err);
        } finally {
          setProjectLoading(false);
        }
      };
      fetchProject();
    }
  }, [projectId, user?._id]);

  // === FETCH USER'S TEAMS (SÉCURISÉ) ===
  useEffect(() => {
    if (!userId || isProjectContext || selectedTeam) return;

    const fetchUserTeams = async () => {
      setLoading(true);
      try {
        // Utiliser la route sécurisée qui utilise l'utilisateur authentifié
        const res = await axios.get(`${API_BASE}/teams/my-teams-events`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });

        // getUserTeamsAndEvents retourne { teams, userCreatedEvents, linkedEvents }
        const userTeams = Array.isArray(res.data.data?.teams) ? res.data.data.teams : [];
        setTeams(userTeams);
        await fetchAllTeamProgress(userTeams);
        
        // Sélectionner automatiquement la première équipe si disponible
        if (userTeams.length > 0) {
          const firstTeam = userTeams[0];
          setSelectedTeam(firstTeam);
          initializePermissionsForTeam(firstTeam);
          await fetchTasksForContext(firstTeam);
        }
      } catch (err: any) {
        console.error('Erreur chargement équipes:', err);
        setTeams([]);
        alert(err.response?.data?.error || 'Unable to load your teams');
      } finally {
        setLoading(false);
      }
    };
    fetchUserTeams();
  }, [fetchTasksForContext, initializePermissionsForTeam, isProjectContext, selectedTeam, userId]);

  // === FETCH PROGRESS FOR ALL TEAMS ===
  const fetchAllTeamProgress = async (teamList: Team[]) => {
    const progressMap: Record<string, number> = {};
    await Promise.all(
      teamList.map(async (team) => {
        try {
          const res = await axios.get(`${API_BASE}/tasks/team/${team._id}`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          });
          const teamTasks = Array.isArray(res.data.data.tasks) ? res.data.data.tasks : [];
          const avg = teamTasks.length > 0
            ? Math.round(teamTasks.reduce((s: number, t: Task) => s + (t.progress || 0), 0) / teamTasks.length)
            : 0;
          progressMap[team._id] = avg;
        } catch {
          progressMap[team._id] = 0;
        }
      })
    );
    setTeamProgress(progressMap);
  };

  // === SELECT TEAM → FETCH TASKS ===
  const selectTeam = useCallback(async (team: Team) => {
    setSelectedTeam(team);
    initializePermissionsForTeam(team);
    await fetchTasksForContext(team);
  }, [fetchTasksForContext, initializePermissionsForTeam]);

  // === SET TEAM BASED ON PROJECT CONTEXT ===
  useEffect(() => {
    if (!isProjectContext || !project || !userId) return;
    if (!project.teams || project.teams.length === 0) {
      setTeams([]);
      setSelectedTeam(null);
      setTasks([]);
      return;
    }

    setTeams(project.teams);
    const projectTeam = project.teams[0];
    if (!selectedTeam || selectedTeam._id !== projectTeam._id) {
      selectTeam(projectTeam);
    } else {
      fetchTasksForContext(projectTeam);
    }
  }, [fetchTasksForContext, isProjectContext, project, selectTeam, selectedTeam, userId]);

  // === OPEN PERMISSIONS MODAL ===
  const openPermissionsModal = () => {
    if (!selectedTeam) return;
    
    // Initialiser les permissions si disponibles
    if (selectedTeam.memberPermissions && selectedTeam.memberPermissions.length > 0) {
      // S'assurer que tous les memberId sont des strings
      const normalizedPermissions: MemberPermission[] = selectedTeam.memberPermissions.map(perm => {
        let memberId: string;
        if (typeof perm.memberId === 'object' && perm.memberId !== null && '_id' in perm.memberId) {
          memberId = (perm.memberId as any)._id;
        } else {
          memberId = String(perm.memberId);
        }
        return {
          memberId,
          permissions: perm.permissions
        };
      });
      setMemberPermissions(normalizedPermissions);
    } else {
      // Créer des permissions par défaut pour tous les membres
      const defaultPermissions: MemberPermission[] = selectedTeam.members.map(member => ({
        memberId: member._id,
        permissions: {
          watch: true,
          create: false,
          edit: false,
          delete: false
        }
      }));
      setMemberPermissions(defaultPermissions);
    }
    setShowPermissionsModal(true);
  };

  // === SAVE PERMISSIONS ===
  const savePermissions = async () => {
    if (!selectedTeam) return;
    
    try {
      // S'assurer que tous les memberId sont des strings
      const permissionsToSend = memberPermissions.map(perm => {
        let memberId: string;
        if (typeof perm.memberId === 'object' && perm.memberId !== null && '_id' in perm.memberId) {
          memberId = (perm.memberId as any)._id;
        } else {
          memberId = String(perm.memberId);
        }
        return {
          memberId,
          permissions: perm.permissions
        };
      });
      
      const res = await axios.put(
        `${API_BASE}/teams/${selectedTeam._id}/permissions`,
        { memberPermissions: permissionsToSend },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      
      // Mettre à jour l'équipe sélectionnée avec les nouvelles permissions
      setSelectedTeam({ ...selectedTeam, memberPermissions: res.data.data.memberPermissions });
      setShowPermissionsModal(false);
      alert('Permissions updated successfully');
    } catch (err: any) {
      console.error('Erreur sauvegarde permissions:', err);
      alert(err.response?.data?.error || 'Erreur lors de la sauvegarde des permissions');
    }
  };

  // === CREATE TASK ===
  const handleCreateTask = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTeam) return;
    try {
      const res = await axios.post(
        `${API_BASE}/tasks/team/${selectedTeam._id}`,
        {
          ...formData,
          subTasks: formData.subTasks.map(s => ({ ...s, assignee: s.assignee || null, status: s.status || 'todo' }))
        },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      const newTask = res.data.data;
      const updated = [...tasks, newTask];
      setTasks(updated);
      organizeTasksByStatus(updated);
      setShowCreateModal(false);
      setFormData({ title: '', description: '', status: 'todo', priority: 'medium', deadline: '', assignee: '', tags: [], subTasks: [] });
    } catch (err: any) {
      alert(err.response?.data?.error || 'Error creating item');
    }
  };

  // === CHECK PERMISSIONS ===
  const hasPermission = (permission: 'create' | 'edit' | 'delete'): boolean => {
    if (!selectedTeam || !user) return false;
    
    // Le créateur de l'équipe a tous les droits
    if (selectedTeam.creator?._id === user._id) return true;
    
    // Vérifier les permissions du membre
    const memberPerm = selectedTeam.memberPermissions?.find(p => {
      const memberId = typeof p.memberId === 'object' && p.memberId !== null && '_id' in p.memberId
        ? (p.memberId as any)._id
        : String(p.memberId);
      return memberId === user._id;
    });
    
    if (!memberPerm) return false; // Par défaut, pas de permission sauf watch (lecture seule)
    
    return memberPerm.permissions[permission] === true;
  };

  // === OPEN EDIT MODAL ===
  const openEditModal = (task: Task) => {
    if (!hasPermission('edit')) {
      alert('You do not have permission to edit this task');
      return;
    }
    setEditingTask(task);
    setFormData({
      title: task.title,
      description: task.description || '',
      status: task.status,
      priority: task.priority,
      deadline: task.deadline ? new Date(task.deadline).toISOString().split('T')[0] : '',
      assignee: task.assignee?._id || '',
      tags: task.tags || [],
      subTasks: task.subTasks.map(st => ({
        title: st.title,
        status: st.status || 'todo',
        assignee: typeof st.assignee === 'object' && st.assignee !== null
          ? (st.assignee as any)._id || ''
          : st.assignee || ''
      }))
    });
    setShowEditModal(true);
  };

  // === UPDATE TASK ===
  const handleUpdateTask = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTeam || !editingTask) return;
    
    if (!hasPermission('edit')) {
      alert('You do not have permission to edit this task');
      return;
    }
    
    try {
      await axios.put(
        `${API_BASE}/tasks/${editingTask._id}`,
        {
          ...formData,
          subTasks: formData.subTasks.map(s => ({ ...s, assignee: s.assignee || null, status: s.status || 'todo' }))
        },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      await selectTeam(selectedTeam);
      setShowEditModal(false);
      setEditingTask(null);
      setFormData({ title: '', description: '', status: 'todo', priority: 'medium', deadline: '', assignee: '', tags: [], subTasks: [] });
    } catch (err: any) {
      alert(err.response?.data?.error || 'Erreur modification');
    }
  };

  // === DELETE TASK ===
  const handleDeleteTask = async (taskId: string) => {
    if (!hasPermission('delete')) {
      alert('You do not have permission to delete this task');
      return;
    }
    
    if (!window.confirm('Are you sure you want to delete this task?')) return;
    
    try {
      await axios.delete(`${API_BASE}/tasks/${taskId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      await selectTeam(selectedTeam!);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Erreur suppression');
    }
  };

  // === UPDATE SUBTASK ===
  const handleUpdateSubTask = async (taskId: string, index: number, updates: { status?: string; title?: string }) => {
    if (!hasPermission('edit')) {
      alert('You do not have permission to edit this task');
      return;
    }
    
    try {
      const task = tasks.find(t => t._id === taskId);
      if (!task) return;
      const updated = [...task.subTasks];
      if (updates.status !== undefined) updated[index].status = updates.status;
      if (updates.title !== undefined) updated[index].title = updates.title;
      await axios.put(`${API_BASE}/tasks/${taskId}`, { subTasks: updated }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      await selectTeam(selectedTeam!);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Error updating');
    }
  };

  const createTimeEstimationVote = async (taskId: string) => {
    try {
      setEstimationLoading(true);
      const res = await axios.post(`${API_BASE}/votes/task/${taskId}/time-estimation`, {
        endDate: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString()
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.data.success) {
        await fetchTimeEstimationVote(taskId);
        const task = tasks.find(t => t._id === taskId);
        if (task) {
          setSelectedTaskForEstimation(task);
          setShowEstimationModal(true);
        }
      }
    } catch (err: any) {
      alert(err.response?.data?.error || 'Error creating estimation vote');
    } finally {
      setEstimationLoading(false);
    }
  };

  const submitTimeEstimationVote = async (voteId: string, optionIndex: number | null = null) => {
    try {
      setEstimationLoading(true);
      
      // Si customHours est fourni, l'utiliser, sinon utiliser l'option sélectionnée
      const payload: any = {};
      if (customHours && customHours.trim()) {
        const hours = parseFloat(customHours);
        if (isNaN(hours) || hours < 0) {
          alert('Veuillez entrer un nombre d\'heures valide');
          setEstimationLoading(false);
          return;
        }
        payload.customHours = hours;
      } else if (optionIndex !== null) {
        payload.selectedOptions = [{ optionIndex, weight: 1 }];
      } else {
        alert('Please select an option or enter a number of hours');
        setEstimationLoading(false);
        return;
      }
      
      await axios.post(`${API_BASE}/votes/${voteId}/vote`, payload, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (selectedTaskForEstimation) {
        await fetchTimeEstimationVote(selectedTaskForEstimation._id);
      }
      setShowEstimationModal(false);
      setSelectedTaskForEstimation(null);
      setSelectedEstimationOption(null);
      setCustomHours('');
    } catch (err: any) {
      alert(err.response?.data?.error || 'Erreur soumission vote');
    } finally {
      setEstimationLoading(false);
    }
  };

  const applyTimeEstimation = async (voteId: string, taskId: string) => {
    try {
      setEstimationLoading(true);
      const res = await axios.post(`${API_BASE}/votes/${voteId}/apply-estimation`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.data.success) {
        alert(`Estimate applied: ${res.data.data.estimatedDays} day(s)`);
        setShowEstimationModal(false);
        setSelectedTaskForEstimation(null);
        setTimeEstimationVotes(prev => {
          const updated = { ...prev };
          delete updated[taskId];
          return updated;
        });
        // Rafraîchir les tâches
        if (selectedTeam) {
          await selectTeam(selectedTeam);
        }
      }
    } catch (err: any) {
      alert(err.response?.data?.error || 'Erreur application estimation');
    } finally {
      setEstimationLoading(false);
    }
  };

  const openEstimationModal = async (task: Task) => {
    setSelectedTaskForEstimation(task);
    await fetchTimeEstimationVote(task._id);
    setShowEstimationModal(true);
  };

  // === DRAG & DROP ===
  const handleDragStart = (e: DragStartEvent) => {
    const task = tasks.find(t => t._id === e.active.id);
    setActiveTask(task || null);
  };

  const handleDragEnd = async (e: DragEndEvent) => {
    const { active, over } = e;
    setActiveTask(null);
    if (!over) return;
    const taskId = active.id as string;
    const overId = over.id as string;

    if (overId.startsWith('column-')) {
      const newStatus = overId.replace('column-', '') as Task['status'];
      const task = tasks.find(t => t._id === taskId);
      if (task?.status === newStatus) return;

      // Vérifier la permission d'édition avant de changer le statut
      if (!hasPermission('edit')) {
        alert('You do not have permission to edit this task');
        return;
      }

      setIsUpdatingStatus(taskId);
      try {
        await axios.put(`${API_BASE}/tasks/${taskId}`, { status: newStatus }, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        await selectTeam(selectedTeam!);
      } catch (err: any) {
        alert(err.response?.data?.error || 'Erreur lors de la modification du statut');
        await selectTeam(selectedTeam!);
      } finally {
        setIsUpdatingStatus(null);
      }
    }
  };

  // === HELPERS ===
  const formatDate = (d: string) => new Date(d).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' });
  const toggleExpand = (id: string) => setExpandedTasks(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const getPriorityColor = (p: string) => {
    const c = { low: 'bg-gray-100 text-gray-700', medium: 'bg-yellow-100 text-yellow-700', high: 'bg-orange-100 text-orange-700', urgent: 'bg-red-100 text-red-700' };
    return c[p as keyof typeof c] || c.medium;
  };
  const getStatusConfig = (s: string) => {
    const cfg: Record<string, any> = {
      todo: { label: 'To Do', color: 'text-orange-700', bg: 'bg-orange-50', border: 'border-orange-200' },
      'in-progress': { label: 'In Progress', color: 'text-blue-700', bg: 'bg-blue-50', border: 'border-blue-200' },
      review: { label: 'Review', color: 'text-yellow-700', bg: 'bg-yellow-50', border: 'border-yellow-200' },
      done: { label: 'Done', color: 'text-green-700', bg: 'bg-green-50', border: 'border-green-200' },
      blocked: { label: 'Blocked', color: 'text-red-700', bg: 'bg-red-50', border: 'border-red-200' },
    };
    return cfg[s] || cfg.todo;
  };

  // === SORTABLE TASK CARD ===
  const SortableTaskCard: React.FC<{ task: Task }> = ({ task }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: task._id });
    const style = { 
      transform: CSS.Transform.toString(transform), 
      transition: isDragging ? 'none' : transition,
      opacity: isDragging ? 0.4 : 1,
      scale: isDragging ? 1.05 : 1
    };
    const expanded = expandedTasks.has(task._id);
    const hasSubs = task.subTasks.length > 0;
    const updating = isUpdatingStatus === task._id;
    const canEdit = hasPermission('edit');
    const canDelete = hasPermission('delete');

    const priorityColors = {
      low: 'from-green-500 to-emerald-500',
      medium: 'from-blue-500 to-cyan-500',
      high: 'from-orange-500 to-amber-500',
      urgent: 'from-red-500 to-rose-500'
    };

    return (
      <div
        ref={setNodeRef}
        style={style}
        className={`group relative bg-white rounded-xl shadow-lg border border-gray-200 p-4 mb-3 cursor-grab active:cursor-grabbing 
          transition-all duration-300 ease-out
          hover:shadow-2xl hover:scale-[1.02] hover:-translate-y-1
          ${isDragging ? 'ring-4 ring-purple-500 ring-opacity-50 shadow-2xl z-50' : ''} 
          ${task.isOverdue ? 'border-l-4 border-l-red-500 bg-red-50/30' : ''} 
          ${updating ? 'opacity-50 pointer-events-none' : ''}
          backdrop-blur-sm bg-gradient-to-br from-white to-gray-50/50`}
        {...attributes}
        {...listeners}
      >
        {/* Gradient overlay on hover */}
        <div className="absolute inset-0 bg-gradient-to-br from-purple-500/0 to-blue-500/0 group-hover:from-purple-500/5 group-hover:to-blue-500/5 rounded-xl transition-all duration-300 pointer-events-none" />
        
        {updating && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/95 backdrop-blur-sm rounded-xl z-10">
            <div className="flex flex-col items-center gap-2">
              <Loader2 className="h-6 w-6 animate-spin text-purple-600" />
              <span className="text-xs text-gray-600">Updating...</span>
            </div>
          </div>
        )}
        
        <div className="relative z-0 flex items-start gap-3">
          <div className="flex-shrink-0 mt-0.5">
            <GripVertical className="h-5 w-5 text-gray-300 group-hover:text-purple-400 transition-colors duration-200" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between mb-2 gap-2">
              <div className="flex-1 min-w-0">
                <h4 className="font-bold text-sm text-gray-900 mb-1.5 line-clamp-2 group-hover:text-purple-700 transition-colors">
                  {task.title}
                </h4>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide bg-gradient-to-r ${priorityColors[task.priority]} text-white shadow-sm`}>
                    {task.priority}
                  </span>
                  {task.isOverdue && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-red-100 text-red-700 border border-red-200">
                      En retard
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200" onClick={(e) => e.stopPropagation()}>
                {canEdit && (
                  <button
                    onClick={(e) => { e.stopPropagation(); openEditModal(task); }}
                    className="p-1.5 hover:bg-purple-100 rounded-lg text-purple-600 hover:text-purple-700 transition-all duration-200 hover:scale-110"
                    title="Edit"
                  >
                    <Edit className="h-4 w-4" />
                  </button>
                )}
                {canDelete && (
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDeleteTask(task._id); }}
                    className="p-1.5 hover:bg-red-100 rounded-lg text-red-600 hover:text-red-700 transition-all duration-200 hover:scale-110"
                    title="Delete"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
            {task.description && (
              <p className="text-xs text-gray-600 mb-3 line-clamp-2 leading-relaxed">
                {task.description}
              </p>
            )}
            <div className="flex flex-wrap gap-2 text-xs mb-3">
              {task.assignee && (
                <div className="flex items-center gap-1.5 px-2 py-1 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border border-purple-100">
                  <div className="w-6 h-6 rounded-full bg-gradient-to-br from-purple-500 to-blue-600 text-white text-[10px] font-bold flex items-center justify-center shadow-sm ring-2 ring-white">
                    {task.assignee.name[0].toUpperCase()}
                  </div>
                  <span className="text-gray-700 font-medium">{task.assignee.name}</span>
                </div>
              )}
              {task.deadline && (
                <span className={`flex items-center gap-1.5 px-2 py-1 rounded-lg border ${
                  task.isOverdue 
                    ? 'bg-red-50 border-red-200 text-red-700' 
                    : 'bg-gray-50 border-gray-200 text-gray-700'
                }`}>
                  <Calendar className={`h-3.5 w-3.5 ${task.isOverdue ? 'text-red-600' : 'text-gray-500'}`} />
                  <span className="font-medium">{formatDate(task.deadline)}</span>
                </span>
              )}
              {hasSubs && (
                <button
                  onClick={(e) => { e.stopPropagation(); toggleExpand(task._id); }}
                  className="flex items-center text-purple-600 hover:text-purple-700"
                >
                  <Hash className="h-3 w-3 mr-1" />
                  {task.subTasks.length}
                </button>
              )}
              {/* Afficher l'estimation depuis estimatedDuration ou depuis le vote d'estimation */}
              {(() => {
                // Priorité à estimatedDuration si disponible
                if (task.estimatedDuration && task.estimatedDuration > 0) {
                  return (
                    <span className="flex items-center text-blue-600">
                      <Clock className="h-3 w-3 mr-1" />
                      {task.estimatedDuration} jour{task.estimatedDuration > 1 ? 's' : ''}
                    </span>
                  );
                }
                // Sinon, afficher l'estimation depuis le vote si disponible
                if (timeEstimationVotes[task._id] && timeEstimationVotes[task._id].results) {
                  const vote = timeEstimationVotes[task._id];
                  const estimatedHours = vote.estimatedHours || vote.results?.averageHours;
                  if (estimatedHours && estimatedHours > 0) {
                    const days = Math.ceil(estimatedHours / 8);
                    return (
                      <span className="flex items-center text-purple-600">
                        <Gauge className="h-3 w-3 mr-1" />
                        {days} jour{days > 1 ? 's' : ''} ({estimatedHours.toFixed(1)}h)
                      </span>
                    );
                  }
                }
                return null;
              })()}
            </div>
            <div className="relative w-full h-2 bg-gray-200 rounded-full overflow-hidden mb-2 shadow-inner">
              <div
                className="h-full bg-gradient-to-r from-purple-500 via-blue-500 to-cyan-500 rounded-full transition-all duration-500 ease-out shadow-sm relative overflow-hidden"
                style={{ width: `${task.progress}%` }}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer" />
              </div>
              {task.progress > 0 && (
                <span className="absolute right-1 top-1/2 -translate-y-1/2 text-[9px] font-bold text-gray-600">
                  {task.progress}%
                </span>
              )}
            </div>
            {hasSubs && expanded && (
              <div className="mt-3 pt-3 border-t space-y-2">
                {task.subTasks.map((sub, i) => {
                  const done = sub.status === 'done';
                  return (
                    <div key={i} className={`p-2 rounded border ${done ? 'bg-green-50 border-green-300' : 'bg-gray-50 border-gray-200'}`}>
                      <div className="flex items-start gap-2">
                        {canEdit && (
                          <button
                            onClick={(e) => { e.stopPropagation(); handleUpdateSubTask(task._id, i, { status: done ? 'todo' : 'done' }); }}
                            className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${done ? 'bg-green-500 border-green-600' : 'bg-white border-gray-300'}`}
                            title={done ? 'Mark as not done' : 'Mark as done'}
                          >
                            {done && <CheckCircle2 className="h-3 w-3 text-white" />}
                          </button>
                        )}
                        <p className={`text-xs flex-1 ${done ? 'text-gray-500 line-through' : 'text-gray-900'}`}>
                          {sub.title}
                        </p>
                        {sub.assignee && (
                          <span className="text-xs text-gray-400 ml-2">
                            {typeof sub.assignee === 'object' ? sub.assignee.name : 'Assigned'}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // === KANBAN COLUMN ===
  interface KanbanColumnProps {
    status: Task['status'];
    tasks: Task[];
  }

  const KanbanColumn: React.FC<KanbanColumnProps> = ({ status, tasks }) => {
    const cfg = getStatusConfig(status);
    const { setNodeRef, isOver } = useDroppable({ id: `column-${status}` });

    return (
      <div
        ref={setNodeRef}
        className={`flex-shrink-0 w-80 ${cfg.bg} rounded-lg border-2 ${
          isOver ? 'border-purple-500 border-4' : cfg.border
        } p-4 transition-all`}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className={`font-bold ${cfg.color}`}>{cfg.label}</h3>
          <span
            className={`px-2 py-0.5 rounded-full text-xs font-semibold ${cfg.bg} ${cfg.color} border ${cfg.border}`}
          >
            {tasks.length}
          </span>
        </div>

        <SortableContext items={tasks.map(t => t._id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-2 min-h-[400px]">
            {tasks.map(task => (
              <SortableTaskCard key={task._id} task={task} />
            ))}
            {tasks.length === 0 && (
              <div className="text-center py-8 text-sm text-gray-400 border-2 border-dashed border-gray-300 rounded-lg">
                Drop here
              </div>
            )}
          </div>
        </SortableContext>
      </div>
    );
  };

  // === LOADING SCREEN ===
  if (loading && !selectedTeam) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-br from-gray-50 to-white">
        <Loader2 className="h-12 w-12 text-purple-600 animate-spin mb-4" />
        <p className="text-lg text-gray-600">Loading your teams...</p>
      </div>
    );
  }

  // === MAIN RENDER ===
  const isCreator = project?.creator?._id === user?._id;

  return (
    <div className={embedded ? '' : 'min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50'}>
      {/* Shared Project Navbar */}
      {!embedded && project && (
        <ProjectNavbar
          project={project}
          projectId={projectId || ''}
          isCreator={isCreator}
        />
      )}

      <div className={embedded ? '' : 'max-w-7xl mx-auto px-4 py-8'}>

        {/* === TÂCHES DE L'ÉQUIPE (TRELLO) === */}
        {isProjectContext && project && (!project.teams || project.teams.length === 0) ? (
          <div className="text-center py-16 bg-white rounded-2xl border-2 border-dashed border-gray-300">
            <Users className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <p className="text-lg text-gray-500">No team linked to this project</p>
            <p className="text-sm text-gray-400 mt-2">Create a team from the project to enable the board.</p>
            {project.creator?._id === user?._id && (
              <button
                onClick={() => navigate(`/projects/${project._id}`)}
                className="mt-4 px-5 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
              >
                Manage project
              </button>
            )}
          </div>
        ) : !selectedTeam && Array.isArray(teams) && teams.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl border-2 border-dashed border-gray-300">
            <Users className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <p className="text-lg text-gray-500">No team joined</p>
            <p className="text-sm text-gray-400 mt-2">Join or create a team to get started</p>
          </div>
        ) : selectedTeam && (

          /* === TÂCHES DE L'ÉQUIPE (TRELLO) === */
          <>
            <div className="mb-6 flex items-center justify-between">
              <div className="flex-1">
                {!isProjectContext && teams.length > 1 && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Select a team</label>
                    <select
                      value={selectedTeam._id}
                      onChange={(e) => {
                        const team = teams.find(t => t._id === e.target.value);
                        if (team) selectTeam(team);
                      }}
                      className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white"
                    >
                      {teams.map(team => (
                        <option key={team._id} value={team._id}>
                          {team.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <h1 className="text-3xl font-bold text-gray-900">{selectedTeam.name}</h1>
                <p className="text-gray-600">{tasks.length} task(s)</p>
              </div>
              <div className="flex gap-3">
                {selectedTeam.creator?._id === user?._id && (
                  <button
                    onClick={openPermissionsModal}
                    className="flex items-center px-5 py-3 bg-gray-100 text-gray-700 rounded-xl shadow-md hover:shadow-lg font-medium hover:bg-gray-200"
                  >
                    <Shield className="h-5 w-5 mr-2" /> Permissions
                  </button>
                )}
                {hasPermission('create') && (
                  <button
                    onClick={() => setShowCreateModal(true)}
                    className="flex items-center px-5 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl shadow-md hover:shadow-lg font-medium"
                  >
                    <Plus className="h-5 w-5 mr-2" /> New Task
                  </button>
                )}
              </div>
            </div>

            {tasks.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-2xl border-2 border-dashed border-gray-300">
                <Tag className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <p className="text-lg text-gray-500">No tasks</p>
                {hasPermission('create') && (
                  <button
                    onClick={() => setShowCreateModal(true)}
                    className="mt-4 px-5 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                  >
                    <Plus className="h-5 w-5 inline mr-2" /> Create Task
                  </button>
                )}
              </div>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCorners}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
              >
                <div className="flex gap-4 overflow-x-auto pb-4" style={{ minHeight: '600px' }}>
                  {(['todo', 'in-progress', 'review', 'done', 'blocked'] as const).map(status => {
                    const columnTasks = tasksByStatus[status] ?? [];
                    return <KanbanColumn key={status} status={status} tasks={columnTasks} />;
                  })}
                </div>

                <DragOverlay dropAnimation={null}>
                  {activeTask && (() => {
                    const priorityGradients: Record<string, string> = {
                      low: 'from-green-500 to-emerald-500',
                      medium: 'from-blue-500 to-cyan-500',
                      high: 'from-orange-500 to-amber-500',
                      urgent: 'from-red-500 to-rose-500'
                    };
                    return (
                      <div className="bg-white/95 backdrop-blur-md p-5 rounded-2xl shadow-2xl border-2 border-purple-500 w-80 transform rotate-3 scale-105 opacity-95">
                        <div className="flex items-start gap-3 mb-3">
                          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-blue-600 flex items-center justify-center text-white font-bold text-lg shadow-lg">
                            {activeTask.title[0].toUpperCase()}
                          </div>
                          <div className="flex-1">
                            <h4 className="font-bold text-sm text-gray-900 mb-1">{activeTask.title}</h4>
                            {activeTask.description && (
                              <p className="text-xs text-gray-600 line-clamp-2">{activeTask.description}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-gradient-to-r ${priorityGradients[activeTask.priority] || 'from-gray-500 to-gray-600'} text-white`}>
                            {activeTask.priority}
                          </span>
                          {activeTask.progress > 0 && (
                            <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-gradient-to-r from-purple-500 to-blue-600 rounded-full"
                                style={{ width: `${activeTask.progress}%` }}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })()}
                </DragOverlay>
              </DndContext>
            )}
          </>
        )}

        {/* === MODAL CRÉATION TÂCHE === */}
        {showCreateModal && selectedTeam && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[95vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
                <h3 className="text-2xl font-bold">
                  Create Task in <span className="text-purple-600">{selectedTeam.name}</span>
                </h3>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <form onSubmit={handleCreateTask} className="p-6 space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Title *</label>
                    <input
                      value={formData.title}
                      onChange={e => setFormData(p => ({ ...p, title: e.target.value }))}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Priority</label>
                    <select
                      value={formData.priority}
                      onChange={e => setFormData(p => ({ ...p, priority: e.target.value as any }))}
                      className="w-full px-4 py-2 border rounded-lg"
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={e => setFormData(p => ({ ...p, description: e.target.value }))}
                    rows={3}
                    className="w-full px-4 py-2 border rounded-lg"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Deadline</label>
                    <input
                      type="date"
                      value={formData.deadline}
                      onChange={e => setFormData(p => ({ ...p, deadline: e.target.value }))}
                      className="w-full px-4 py-2 border rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Assign to</label>
                    <select
                      value={formData.assignee}
                      onChange={e => setFormData(p => ({ ...p, assignee: e.target.value }))}
                      className="w-full px-4 py-2 border rounded-lg"
                    >
                      <option value="">None</option>
                      {selectedTeam.members.map(m => (
                        <option key={m._id} value={m._id}>{m.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Tags (comma separated)</label>
                  <input
                    value={formData.tags.join(', ')}
                    onChange={e => setFormData(p => ({ ...p, tags: e.target.value.split(',').map(t => t.trim()).filter(Boolean) }))}
                    className="w-full px-4 py-2 border rounded-lg"
                    placeholder="ui, urgent"
                  />
                </div>
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="text-sm font-medium">Sub-tasks</label>
                    <button
                      type="button"
                      onClick={() => setFormData(p => ({ ...p, subTasks: [...p.subTasks, { title: '', status: 'todo', assignee: '' }] }))}
                      className="text-sm text-purple-600 flex items-center"
                    >
                      <Plus className="h-4 w-4 mr-1" /> Add
                    </button>
                  </div>
                  <div className="space-y-2 max-h-48 overflow-y-auto p-3 bg-gray-50 rounded-lg border">
                    {formData.subTasks.length === 0 ? (
                      <p className="text-center text-sm text-gray-500 py-3">No sub-tasks</p>
                    ) : (
                      formData.subTasks.map((s, i) => (
                        <div key={i} className="flex gap-2 bg-white p-2 rounded border">
                          <input
                            value={s.title}
                            onChange={e => {
                              const ns = [...formData.subTasks];
                              ns[i].title = e.target.value;
                              setFormData(p => ({ ...p, subTasks: ns }));
                            }}
                            className="flex-1 px-3 py-1 border rounded text-sm"
                            placeholder="Title"
                          />
                          <select
                            value={s.assignee}
                            onChange={e => {
                              const ns = [...formData.subTasks];
                              ns[i].assignee = e.target.value;
                              setFormData(p => ({ ...p, subTasks: ns }));
                            }}
                            className="px-3 py-1 border rounded text-sm"
                          >
                            < option value="">None</option>
                            {selectedTeam.members.map(m => (
                              <option key={m._id} value={m._id}>{m.name}</option>
                            ))}
                          </select>
                          <button
                            type="button"
                            onClick={() => setFormData(p => ({ ...p, subTasks: p.subTasks.filter((_, idx) => idx !== i) }))}
                            className="p-1 text-red-500 hover:bg-red-50 rounded"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                </div>
                <div className="flex gap-3 pt-4 border-t">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:opacity-90 font-medium flex items-center justify-center"
                  >
                    <Plus className="h-5 w-5 mr-2" /> Create
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* === MODAL ÉDITION TÂCHE === */}
        {showEditModal && selectedTeam && editingTask && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[95vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
                <h3 className="text-2xl font-bold">
                  Edit Task - <span className="text-purple-600">{editingTask.title}</span>
                </h3>
                <button
                  onClick={() => { setShowEditModal(false); setEditingTask(null); }}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <form onSubmit={handleUpdateTask} className="p-6 space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Title *</label>
                    <input
                      value={formData.title}
                      onChange={e => setFormData(p => ({ ...p, title: e.target.value }))}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Priority</label>
                    <select
                      value={formData.priority}
                      onChange={e => setFormData(p => ({ ...p, priority: e.target.value as any }))}
                      className="w-full px-4 py-2 border rounded-lg"
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={e => setFormData(p => ({ ...p, description: e.target.value }))}
                    rows={3}
                    className="w-full px-4 py-2 border rounded-lg"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Status</label>
                    <select
                      value={formData.status}
                      onChange={e => setFormData(p => ({ ...p, status: e.target.value as any }))}
                      className="w-full px-4 py-2 border rounded-lg"
                    >
                      <option value="todo">To Do</option>
                      <option value="in-progress">In Progress</option>
                      <option value="review">Review</option>
                      <option value="done">Done</option>
                      <option value="blocked">Blocked</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Deadline</label>
                    <input
                      type="date"
                      value={formData.deadline}
                      onChange={e => setFormData(p => ({ ...p, deadline: e.target.value }))}
                      className="w-full px-4 py-2 border rounded-lg"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Assign to</label>
                  <select
                    value={formData.assignee}
                    onChange={e => setFormData(p => ({ ...p, assignee: e.target.value }))}
                    className="w-full px-4 py-2 border rounded-lg"
                  >
                    <option value="">None</option>
                    {selectedTeam.members.map(m => (
                      <option key={m._id} value={m._id}>{m.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Tags (comma separated)</label>
                  <input
                    value={formData.tags.join(', ')}
                    onChange={e => setFormData(p => ({ ...p, tags: e.target.value.split(',').map(t => t.trim()).filter(Boolean) }))}
                    className="w-full px-4 py-2 border rounded-lg"
                    placeholder="ui, urgent"
                  />
                </div>
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="text-sm font-medium">Sub-tasks</label>
                    <button
                      type="button"
                      onClick={() => setFormData(p => ({ ...p, subTasks: [...p.subTasks, { title: '', status: 'todo', assignee: '' }] }))}
                      className="text-sm text-purple-600 flex items-center"
                    >
                      <Plus className="h-4 w-4 mr-1" /> Add
                    </button>
                  </div>
                  <div className="space-y-2 max-h-48 overflow-y-auto p-3 bg-gray-50 rounded-lg border">
                    {formData.subTasks.length === 0 ? (
                      <p className="text-center text-sm text-gray-500 py-3">No sub-tasks</p>
                    ) : (
                      formData.subTasks.map((s, i) => (
                        <div key={i} className="flex gap-2 bg-white p-2 rounded border">
                          <input
                            value={s.title}
                            onChange={e => {
                              const ns = [...formData.subTasks];
                              ns[i].title = e.target.value;
                              setFormData(p => ({ ...p, subTasks: ns }));
                            }}
                            className="flex-1 px-3 py-1 border rounded text-sm"
                            placeholder="Title"
                          />
                          <select
                            value={s.status}
                            onChange={e => {
                              const ns = [...formData.subTasks];
                              ns[i].status = e.target.value;
                              setFormData(p => ({ ...p, subTasks: ns }));
                            }}
                            className="px-3 py-1 border rounded text-sm"
                          >
                            <option value="todo">To Do</option>
                            <option value="in-progress">In Progress</option>
                            <option value="done">Done</option>
                            <option value="blocked">Blocked</option>
                          </select>
                          <select
                            value={s.assignee}
                            onChange={e => {
                              const ns = [...formData.subTasks];
                              ns[i].assignee = e.target.value;
                              setFormData(p => ({ ...p, subTasks: ns }));
                            }}
                            className="px-3 py-1 border rounded text-sm"
                          >
                            <option value="">None</option>
                            {selectedTeam.members.map(m => (
                              <option key={m._id} value={m._id}>{m.name}</option>
                            ))}
                          </select>
                          <button
                            type="button"
                            onClick={() => setFormData(p => ({ ...p, subTasks: p.subTasks.filter((_, idx) => idx !== i) }))}
                            className="p-1 text-red-500 hover:bg-red-50 rounded"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                </div>
                <div className="flex gap-3 pt-4 border-t">
                  <button
                    type="button"
                    onClick={() => { setShowEditModal(false); setEditingTask(null); }}
                    className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:opacity-90 font-medium flex items-center justify-center"
                  >
                    <Save className="h-5 w-5 mr-2" /> Save
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* === MODAL GESTION PERMISSIONS === */}
        {showPermissionsModal && selectedTeam && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[95vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
                <h3 className="text-2xl font-bold">
                  Manage Permissions - <span className="text-purple-600">{selectedTeam.name}</span>
                </h3>
                <button
                  onClick={() => setShowPermissionsModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="p-6">
                <p className="text-sm text-gray-600 mb-6">
                  Select permissions for each team member.
                </p>

                {/* Liste des membres avec checkboxes */}
                <div className="space-y-4">
                  {selectedTeam.members.map(member => {
                    const perm = memberPermissions.find(p => p.memberId === member._id);
                    if (!perm) return null;
                    
                    return (
                      <div key={member._id} className="bg-white rounded-lg p-5 border-2 border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-blue-600 text-white font-bold text-lg flex items-center justify-center">
                            {member.name[0].toUpperCase()}
                          </div>
                          <div className="flex-1">
                            <p className="font-semibold text-gray-900 text-lg">{member.name}</p>
                            <p className="text-sm text-gray-500">{member.email}</p>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          {[
                            { key: 'watch', label: 'Read only', description: 'View tasks', color: 'text-gray-600' },
                            { key: 'create', label: 'Create', description: 'Create tasks', color: 'text-blue-600' },
                            { key: 'edit', label: 'Edit', description: 'Edit tasks', color: 'text-yellow-600' },
                            { key: 'delete', label: 'Delete', description: 'Delete tasks', color: 'text-red-600' }
                          ].map(permission => (
                            <label key={permission.key} className="flex flex-col gap-2 p-3 rounded-lg border-2 border-gray-200 hover:border-purple-400 cursor-pointer transition-colors bg-gray-50 hover:bg-purple-50">
                              <div className="flex items-center gap-2">
                                <input
                                  type="checkbox"
                                  checked={perm.permissions[permission.key as keyof typeof perm.permissions]}
                                  onChange={(e) => {
                                    const updated = memberPermissions.map(p =>
                                      p.memberId === member._id
                                        ? {
                                            ...p,
                                            permissions: {
                                              ...p.permissions,
                                              [permission.key]: e.target.checked
                                            }
                                          }
                                        : p
                                    );
                                    setMemberPermissions(updated);
                                  }}
                                  className="w-5 h-5 text-purple-600 rounded focus:ring-purple-500 focus:ring-2"
                                />
                                <span className={`text-sm font-semibold ${permission.color}`}>{permission.label}</span>
                              </div>
                              <span className="text-xs text-gray-500 ml-7">{permission.description}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="flex gap-3 pt-6 border-t mt-6">
                  <button
                    type="button"
                    onClick={() => setShowPermissionsModal(false)}
                    className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={savePermissions}
                    className="flex-1 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:opacity-90 font-medium flex items-center justify-center"
                  >
                    <Shield className="h-5 w-5 mr-2" /> Enregistrer les permissions
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Time Estimation Modal */}
        {showEstimationModal && selectedTaskForEstimation && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-lg w-full shadow-xl">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Estimation de temps</h3>
                  <p className="text-sm text-gray-600 mt-1">{selectedTaskForEstimation.title}</p>
                </div>
                <button
                  onClick={() => {
                    setShowEstimationModal(false);
                    setSelectedTaskForEstimation(null);
                    setSelectedEstimationOption(null);
                    setCustomHours('');
                  }}
                  className="text-gray-500 hover:text-gray-700 p-1 rounded transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="p-6">
                {timeEstimationVotes[selectedTaskForEstimation._id] ? (
                  <div className="space-y-4">
                    {(() => {
                      const vote = timeEstimationVotes[selectedTaskForEstimation._id];
                      const isActive = vote.status === 'active';
                      const hasVoted = vote.userHasVoted;
                      const canVote = vote.canVote?.canVote !== false;

                      return (
                        <>
                          {/* Options prédéfinies */}
                          <div className="space-y-2 mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">Preset options:</label>
                            {vote.options.map((option: any, index: number) => {
                              return (
                                <label
                                  key={index}
                                  className={`flex items-center gap-3 p-3 border-2 rounded-lg cursor-pointer transition-colors flex-1 ${
                                    selectedEstimationOption === index && !customHours
                                      ? 'border-blue-500 bg-blue-50'
                                      : 'border-gray-200 hover:border-gray-300'
                                  } ${!isActive || hasVoted ? 'opacity-60 cursor-not-allowed' : ''}`}
                                >
                                  <input
                                    type="radio"
                                    name="estimation"
                                    checked={selectedEstimationOption === index && !customHours}
                                    onChange={() => {
                                      setSelectedEstimationOption(index);
                                      setCustomHours('');
                                    }}
                                    disabled={!isActive || hasVoted || !canVote}
                                    className="h-4 w-4 text-blue-600"
                                  />
                                  <span className="flex-1 text-sm font-medium">{option.text}</span>
                                </label>
                              );
                            })}
                          </div>

                          {/* Saisie manuelle */}
                          <div className="border-t border-gray-200 pt-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">Ou saisir manuellement :</label>
                            <div className="flex items-center gap-2">
                              <input
                                type="number"
                                min="0"
                                step="0.5"
                                value={customHours}
                                onChange={(e) => {
                                  setCustomHours(e.target.value);
                                  if (e.target.value) {
                                    setSelectedEstimationOption(null);
                                  }
                                }}
                                placeholder="Number of hours"
                                disabled={!isActive || hasVoted || !canVote}
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                              />
                              <span className="text-sm text-gray-600">heures</span>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">You can enter a decimal number (e.g. 2.5 for 2h30)</p>
                          </div>

                          {/* Affichage de la moyenne */}
                          {vote.resultsVisibility === 'realtime' && (vote.estimatedHours !== undefined && vote.estimatedHours !== null || (vote.results?.averageHours !== undefined && vote.results?.averageHours !== null)) && (
                            <div className="mt-4 pt-4 border-t border-gray-200 bg-blue-50 rounded-lg p-4">
                              <div className="flex items-center gap-3">
                                <Gauge className="h-5 w-5 text-blue-600" />
                                <div className="flex-1">
                                  <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Estimation moyenne</span>
                                  <div className="flex items-center gap-2 mt-1">
                                    {vote.estimatedHours !== undefined && vote.estimatedHours !== null ? (
                                      <>
                                        <span className="text-lg font-bold text-gray-900">
                                          {vote.estimatedHours.toFixed(1)} heures
                                        </span>
                                        <span className="text-sm text-gray-600">
                                          ({Math.ceil(vote.estimatedHours / 8)} jour{Math.ceil(vote.estimatedHours / 8) > 1 ? 's' : ''})
                                        </span>
                                      </>
                                    ) : vote.results?.averageHours !== undefined && vote.results?.averageHours !== null ? (
                                      <>
                                        <span className="text-lg font-bold text-gray-900">
                                          {vote.results.averageHours.toFixed(1)} heures
                                        </span>
                                        <span className="text-sm text-gray-600">
                                          ({Math.ceil(vote.results.averageHours / 8)} jour{Math.ceil(vote.results.averageHours / 8) > 1 ? 's' : ''})
                                        </span>
                                      </>
                                    ) : null}
                                  </div>
                                  <div className="text-xs text-gray-500 mt-1">
                                    {vote.results?.totalVotes || 0} vote{(vote.results?.totalVotes || 0) > 1 ? 's' : ''}
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}

                          {isActive && !hasVoted && canVote && (
                            <button
                              onClick={() => {
                                submitTimeEstimationVote(vote._id, selectedEstimationOption ?? null);
                              }}
                              disabled={(selectedEstimationOption === null && !customHours) || estimationLoading}
                              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed font-medium"
                            >
                              {estimationLoading ? 'Envoi...' : 'Soumettre mon estimation'}
                            </button>
                          )}

                          {hasVoted && (
                            <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
                              ✓ You already voted
                            </div>
                          )}

                          {vote.results && vote.results.winner && !vote.results.winner.isTie && (
                            <div className="mt-4 pt-4 border-t border-gray-200">
                              <div className="flex items-center justify-between">
                                <div>
                                  <span className="text-sm text-gray-600">Estimation moyenne :</span>
                                  <p className="text-lg font-semibold text-gray-900 mt-1">
                                    {vote.results.winner.optionText}
                                  </p>
                                </div>
                                {(() => {
                                  const taskCreatorId = typeof selectedTaskForEstimation.createdBy === 'object' 
                                    ? selectedTaskForEstimation.createdBy?._id 
                                    : selectedTaskForEstimation.createdBy;
                                  return taskCreatorId === userId && (
                                    <button
                                      onClick={() => applyTimeEstimation(vote._id, selectedTaskForEstimation._id)}
                                      disabled={estimationLoading}
                                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:bg-gray-300 text-sm font-medium"
                                    >
                                      {estimationLoading ? 'Application...' : 'Appliquer'}
                                    </button>
                                  );
                                })()}
                              </div>
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Clock className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 mb-4">No estimation votes for this task</p>
                    <button
                      onClick={() => createTimeEstimationVote(selectedTaskForEstimation._id)}
                      disabled={estimationLoading}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-300 font-medium"
                    >
                      {estimationLoading ? 'Creating...' : 'Create an estimation vote'}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamTasksPage;